import { useNavigate } from "react-router-dom";
import CommonImages from "@base/assets/images";
import InsuranceBadge from "@components/CommonStyles/InsuranceBadge";
import TagSpecialPackageStatus from "@components/CommonStyles/TagSpecialPackageStatus";
import { ESpecialPackageStatus } from "@base/enums";
import { isExtraFlagOn, parseExtraInfo } from "@base/helpers/extraInfo";
import { ISpecialPackage } from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import { baseRoutes } from "@base/routes/baseRoutes";

interface IProps {
  specialPackage: ISpecialPackage;
}

/**
 * A package on the home page, laid out as on mobile: who it belongs to and where, then the
 * picture beside its name and status. The whole card opens the package.
 */
export default function MyPackageCard({ specialPackage }: IProps) {
  //! state
  const navigate = useNavigate();
  // Some insurers keep the selling site out of sight; the package config says so.
  const isSiteHidden = isExtraFlagOn(parseExtraInfo(specialPackage.extra_info).site_info_hidden);
  const siteName = [specialPackage.site?.name, specialPackage.site_branch_name].filter(Boolean).join(", ");

  //! render
  return (
    <button
      type="button"
      onClick={() => navigate(baseRoutes.userPackageDetail.replace(":key", specialPackage.key ?? ""))}
      className="w-full h-full text-left bg-common-white rounded-2xl p-4 flex flex-col justify-start gap-3 border border-common-divider shadow-[0_2px_12px_rgba(16,24,40,0.08)] hover:border-common-primary transition-colors"
    >
      <div className="w-full flex justify-between items-start gap-2">
        <div className="min-w-0 flex flex-col gap-1">
          <span className="text-common-maintext text-base leading-6 font-semibold font-['Inter'] uppercase line-clamp-1">
            {specialPackage.patient_record?.patient_name}
          </span>
          {/* The line is kept, empty, when there is no site to name, so every card lines up. */}
          <span className="text-common-subtext text-sm leading-5 font-normal font-['Inter'] line-clamp-1 min-h-5">
            {!isSiteHidden && siteName ? siteName : " "}
          </span>
        </div>

        <InsuranceBadge specialPackage={specialPackage} />
      </div>

      <div className="w-full border-t border-dashed border-common-disable" />

      {/* The picture and the text are the same height: the name starts at the picture's top edge and
          the status ends at its bottom edge. Whichever is taller sets the height of both. */}
      <div className="w-full flex items-stretch gap-3">
        <div className="relative w-[45%] shrink-0 aspect-[16/9] self-stretch rounded-lg overflow-hidden bg-common-background">
          <img
            className="absolute inset-0 w-full h-full object-cover"
            src={specialPackage.thumbnail || CommonImages.DrafPackage}
            alt=""
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = CommonImages.DrafPackage;
            }}
          />
        </div>

        <div className="min-w-0 flex flex-col items-start justify-between gap-1">
          {/* Always two lines tall, so the status sits at the same height on every card. */}
          <span
            className="text-common-maintext text-base leading-6 font-semibold font-['Inter'] uppercase line-clamp-2 min-h-12"
            title={specialPackage.package_name}
          >
            {specialPackage.package_name}
          </span>
          <TagSpecialPackageStatus status={specialPackage.status as ESpecialPackageStatus} size="md" />
        </div>
      </div>
    </button>
  );
}
